/**
 * Deliverable readiness service (US-030).
 *
 * Pure functions that score a deliverable against the NIL Go submission
 * shape. The router exposes the result via `deliverable.readiness`; the
 * deal-level rollup (US-033) sums across all of a deal's deliverables.
 *
 * Status meanings:
 *   - `ok`           — every category-required field is filled AND, if the
 *                       deliverable is COMPLETED, at least one proof is
 *                       attached (or skip-proof was logged with a reason).
 *   - `needs_detail` — at least one category-required field is missing.
 *   - `needs_proof`  — COMPLETED with neither a proof nor a logged skip.
 *
 * Design notes:
 *   - Pure: no DB or network. Caller passes a snapshot. Makes the rules
 *     trivially unit-testable and reusable from both the router and any
 *     future SSR pre-execution (US-033's deal-level readiness panel).
 *   - Rules are sourced from `@app/validators` so the form, the API
 *     boundary, and this service never drift.
 *   - `metadata` is the raw JSON we persist on `Deliverable.metadata`.
 *     Stored as a string in Postgres; this service accepts either a
 *     pre-parsed record or a raw JSON string and handles malformed JSON
 *     by reporting `needs_detail` rather than throwing.
 */
import { ACTIVITY_METADATA_SCHEMAS, type ActivityCategory } from '@app/validators';

export type DeliverableReadinessStatus = 'ok' | 'needs_detail' | 'needs_proof';

export interface DeliverableReadinessInput {
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  activityCategory: ActivityCategory | null;
  metadata: Record<string, unknown> | string | null;
  proofCount: number;
  skipProofReason: string | null;
}

export interface DeliverableReadinessResult {
  status: DeliverableReadinessStatus;
  missingFields: string[];
}

function parseMetadata(metadata: DeliverableReadinessInput['metadata']): Record<string, unknown> {
  if (metadata == null) return {};
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return metadata;
}

export function computeDeliverableReadiness(
  input: DeliverableReadinessInput,
): DeliverableReadinessResult {
  // CANCELLED deliverables aren't counted against the deal — treat as ok so
  // they don't trip the NIL Go readiness rollup.
  if (input.status === 'CANCELLED') {
    return { status: 'ok', missingFields: [] };
  }

  // No category yet → biggest missing detail of all. Surface that as the
  // sole missing field so the UI nudges the athlete to pick one.
  if (!input.activityCategory) {
    return { status: 'needs_detail', missingFields: ['activityCategory'] };
  }

  const schema = ACTIVITY_METADATA_SCHEMAS[input.activityCategory];
  const parsed = schema.safeParse(parseMetadata(input.metadata));
  if (!parsed.success) {
    const missingFields = parsed.error.issues.map((i) =>
      i.path.length ? i.path.join('.') : '(metadata)',
    );
    return { status: 'needs_detail', missingFields };
  }

  // Category-specific fields are all populated. Now the proof rule kicks
  // in only once the deliverable is COMPLETED — PENDING / IN_PROGRESS rows
  // are ok-but-not-done and shouldn't be nagged for proof yet.
  if (input.status === 'COMPLETED' && input.proofCount === 0 && !input.skipProofReason) {
    return { status: 'needs_proof', missingFields: [] };
  }

  return { status: 'ok', missingFields: [] };
}
